import React from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Autocomplete, Box, Grid, MenuItem, Paper, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import AppForm from '@/__core/components/AppForm';
import DeleteConfirmationModal from '@/__core/components/DeleteConfirmationModal';
import FileAttachmentField from '@/__core/components/FileAttachmentField';
import { UserOptionDisplay } from '@/__core/components';
import { LabeledField } from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import axiosInstance from '@/setup/axios/axiosInstance';
import {
  deleteDocument,
  getContentTypeId,
  getDocumentsByObject,
  type Document,
} from '@/modules/documents/redux/DocumentCRUD';
import AddButtonAutocomplete from '@/__core/hocs/AddButtonAutocomplete';
import ItemSupplierAddModal from '@/modules/inventory/items/components/modals/ItemSupplierAddModal';
import { createItemSupplierOption } from '@/modules/inventory/items/models/ItemModel';
import { getItemUomBackendOptions } from '@/modules/inventory/items/redux/ItemUomCRUD';
import useRequestMaterialItems from '@/modules/inventory/requestMaterial/hooks/useRequestMaterialItems';
import useRequestMaterialWarehouses from '@/modules/inventory/requestMaterial/hooks/useRequestMaterialWarehouses';

import { RequestMaterialEmployeeOption } from '@/modules/inventory/requestMaterial/types';

import { InventoryReceiptFormData, ReceiptLine } from '../types';
import ReceiptLinesTable from './ReceiptLinesTable';

interface ReceiptFormProps {
  formData: InventoryReceiptFormData;
  receiptId?: string;
  statusOptions: Array<{ id?: string; name: string; code?: string; color?: string; is_default?: boolean; is_posted?: boolean }>;
  purchaseOptions: Array<{ id: string; name: string }>;
  returnSourceOptions: Array<{ id: string; name: string }>;
  supplierOptions: Array<{ id: string; name: string; code?: string }>;
  employeeOptions: RequestMaterialEmployeeOption[];
  employeeLoading?: boolean;
  purchaseLoading?: boolean;
  returnSourceLoading?: boolean;
  supplierLoading?: boolean;
  saving?: boolean;
  canEdit?: boolean;
  canEditPosted?: boolean;
  isDirty?: boolean;
  embedded?: boolean;
  onPurchaseSearch: (search: string) => void;
  onReturnSourceSearch: (search: string) => void;
  onSupplierSearch: (search: string) => void;
  onInitializeForm?: (updates: Partial<InventoryReceiptFormData>) => void;
  onChangeForm: (updates: Partial<InventoryReceiptFormData>) => void;
  onChangeLine: (lineId: string, updates: Partial<ReceiptLine>) => void;
  onPendingFilesChange?: (hasPendingFiles: boolean) => void;
  onAttachmentError?: (message: string) => void;
  onSave: (files: File[]) => Promise<string | boolean | void> | string | boolean | void;
}

interface WarehouseAutocompleteProps {
  companyId: string;
  value?: string;
  warehouseName?: string;
  disabled?: boolean;
  onInitialize?: (warehouseId: string, warehouseName: string) => void;
  onChange: (warehouseId: string | null, warehouseName?: string | null) => void;
}

interface AddOptionCustomDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<any>;
  loading?: boolean;
}

const SupplierAddModal: React.FC<AddOptionCustomDialogProps> = (props) => <ItemSupplierAddModal {...props} />;

const getStatusValue = (status: InventoryReceiptFormData['status']) => {
  if (typeof status === 'object' && status !== null) {
    return String(status.id || status.code || '').trim();
  }
  return String(status || '').trim();
};

const resolveStatusOption = (
  statusOptions: ReceiptFormProps['statusOptions'],
  formData: InventoryReceiptFormData,
) => {
  const statusValue = getStatusValue(formData.status);
  const statusCode = String(
    (typeof formData.status === 'object' && formData.status !== null ? formData.status.code : '') ||
    formData.status_code ||
    '',
  ).trim();
  const statusName = String(
    (typeof formData.status === 'object' && formData.status !== null ? formData.status.name : '') ||
    formData.status_display ||
    '',
  ).trim();

  return statusOptions.find((option) => (
    option.id === statusValue ||
    option.code === statusValue ||
    option.id === statusCode ||
    option.code === statusCode ||
    option.name === statusName
  )) || null;
};

function WarehouseAutocomplete({
  companyId,
  value,
  warehouseName,
  disabled = false,
  onInitialize,
  onChange,
}: WarehouseAutocompleteProps) {
  const { warehouseOptions, loading } = useRequestMaterialWarehouses({
    companyId: companyId || '',
    skip: !companyId,
    onError: () => {},
  });

  const effectiveOptions = (warehouseOptions && warehouseOptions.length > 0)
    ? warehouseOptions
    : (value ? [{ id: value, name: warehouseName || value }] : []);

  React.useEffect(() => {
    if (!value && effectiveOptions.length > 0 && !loading) {
      const defaultStore = effectiveOptions.find((opt) => opt.is_default);
      if (defaultStore) {
        onInitialize?.(defaultStore.id, defaultStore.name);
      }
    }
  }, [effectiveOptions, loading, value, onInitialize]);

  return (
    <AddButtonAutocomplete
      options={effectiveOptions}
      value={effectiveOptions.find((option) => option.id === value) || (value ? { id: value, name: warehouseName || value } : null)}
      onChange={(_event, selectedValue) => {
        const selected = selectedValue as { id: string; name: string } | null;
        onChange(selected?.id || null, selected?.name || null);
      }}
      placeholder='Chọn kho nhập'
      loading={loading}
      noOptionsText='Không tìm thấy kho'
      disabled={disabled}
      fullWidth
    />
  );
}

export default function ReceiptForm({
  formData,
  receiptId,
  statusOptions,
  purchaseOptions,
  returnSourceOptions,
  supplierOptions,
  employeeOptions,
  employeeLoading = false,
  purchaseLoading = false,
  returnSourceLoading = false,
  supplierLoading = false,
  saving = false,
  canEdit = true,
  canEditPosted = false,
  isDirty = false,
  embedded = false,
  onPurchaseSearch,
  onReturnSourceSearch,
  onSupplierSearch,
  onInitializeForm,
  onChangeForm,
  onChangeLine,
  onPendingFilesChange,
  onAttachmentError,
  onSave,
}: ReceiptFormProps) {
  const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = React.useState<Document[]>([]);
  const [loadingDocuments, setLoadingDocuments] = React.useState(false);
  const [pendingDeleteType, setPendingDeleteType] = React.useState<'file' | 'document' | null>(null);
  const [pendingDeleteIndex, setPendingDeleteIndex] = React.useState<number | null>(null);
  const [pendingDeleteDocId, setPendingDeleteDocId] = React.useState<string | null>(null);
  const [manualSupplierOptions, setManualSupplierOptions] = React.useState<Array<{ id: string; name: string; code?: string }>>([]);
  const effectiveSupplierOptions = React.useMemo(() => {
    const merged = [...supplierOptions, ...manualSupplierOptions];
    const unique = new Map<string, { id: string; name: string; code?: string }>();
    merged.forEach((supplier) => {
      if (!unique.has(supplier.id)) {
        unique.set(supplier.id, supplier);
      }
    });
    return Array.from(unique.values());
  }, [supplierOptions, manualSupplierOptions]);

  const selectedPurchase = purchaseOptions.find((option) => option.id === formData.purchase_id) || null;
  const selectedSupplier = effectiveSupplierOptions.find((option) => option.id === formData.supplier_id) || null;
  const selectedEmployee = employeeOptions.find((option) => option.id === formData.employee_id)
    || (formData.employee_id ? { id: formData.employee_id, name: formData.employee_name || '' } : null);
  const isFromPurchase = formData.receipt_type === 'from_purchase';
  const isReturn = formData.receipt_type === 'return';
  const selectedReturnSource = returnSourceOptions.find((option) => option.id === formData.return_source_issue_id) || null;
  const currentStatus = React.useMemo(
    () => resolveStatusOption(statusOptions, formData),
    [formData.status, formData.status_code, formData.status_display, statusOptions],
  );
  const statusSelectValue = currentStatus?.id || getStatusValue(formData.status);
  const effectiveStatusOptions = React.useMemo(() => {
    const normalizedOptions = statusOptions.filter((status) => Boolean(status.id));

    if (!statusSelectValue || normalizedOptions.some((status) => status.id === statusSelectValue)) {
      return normalizedOptions;
    }

    return [
      ...normalizedOptions,
      {
        id: statusSelectValue,
        name: formData.status_display || (typeof formData.status === 'object' ? formData.status.name : '') || statusSelectValue,
        code: formData.status_code || (typeof formData.status === 'object' ? formData.status.code : '') || '',
        color: formData.status_color || (typeof formData.status === 'object' ? formData.status.color : ''),
      },
    ];
  }, [formData.status, formData.status_code, formData.status_color, formData.status_display, statusOptions, statusSelectValue]);
  const isPosted = formData.is_posted === true
    || (typeof formData.status === 'object' && formData.status !== null && formData.status.is_posted === true);
  const isReadOnly = !canEdit || (isPosted && !canEditPosted);
  const receivedDate = formData.received_at ? new Date(formData.received_at) : null;
  const defaultStatusId = React.useMemo(
    () => effectiveStatusOptions.find((status) => status.is_default)?.id || effectiveStatusOptions[0]?.id || '',
    [effectiveStatusOptions],
  );
  const [uomOptions, setUomOptions] = React.useState<Array<{ id: string; name: string; code: string }>>([]);
  const { itemOptions, loading: itemLoading, searchLoading: itemSearchLoading, searchItems: searchItemOptions, addOption: addItemOption } = useRequestMaterialItems({
    companyId: formData.company_id,
    skip: !formData.company_id,
    onError: () => {},
  });

  React.useEffect(() => {
    let active = true;

    if (!receiptId) {
      setUploadedDocuments([]);
      setLoadingDocuments(false);
      return () => { active = false; };
    }

    setLoadingDocuments(true);
    void (async () => {
      try {
        const contentTypeId = await getContentTypeId('receipt');
        const documents = await getDocumentsByObject(contentTypeId, receiptId);
        if (active) {
          setUploadedDocuments(documents);
        }
      } catch {
        if (active) {
          onAttachmentError?.('Không thể tải danh sách file đính kèm.');
        }
      } finally {
        if (active) {
          setLoadingDocuments(false);
        }
      }
    })();

    return () => { active = false; };
  }, [onAttachmentError, receiptId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setSelectedFiles((current) => {
      const nextFiles = [...current, ...files];
      onPendingFilesChange?.(nextFiles.length > 0);
      return nextFiles;
    });
    event.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    if (isReadOnly) return;
    setPendingDeleteType('file');
    setPendingDeleteIndex(index);
  };

  const handleRemoveUploadedDocument = (documentId: string) => {
    if (isReadOnly) return;
    setPendingDeleteType('document');
    setPendingDeleteDocId(documentId);
  };

  const confirmDeleteAttachment = async () => {
    if (pendingDeleteType === 'file' && pendingDeleteIndex !== null) {
      setSelectedFiles((current) => {
        const nextFiles = current.filter((_file, fileIndex) => fileIndex !== pendingDeleteIndex);
        onPendingFilesChange?.(nextFiles.length > 0);
        return nextFiles;
      });
    }
    if (pendingDeleteType === 'document' && pendingDeleteDocId !== null) {
      try {
        await deleteDocument(pendingDeleteDocId);
        setUploadedDocuments((current) => current.filter((document) => document.id !== pendingDeleteDocId));
      } catch {
        onAttachmentError?.('Không thể xóa file đính kèm.');
      }
    }
    setPendingDeleteType(null);
    setPendingDeleteIndex(null);
    setPendingDeleteDocId(null);
  };

  const cancelDeleteAttachment = () => {
    setPendingDeleteType(null);
    setPendingDeleteIndex(null);
    setPendingDeleteDocId(null);
  };

  const handleDocumentDownload = async (documentId: string, preview = false) => {
    try {
      const response = await axiosInstance.get(`documents/${documentId}/download/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);

      if (preview) {
        window.open(url, '_blank', 'noopener,noreferrer');
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        return;
      }

      const document = uploadedDocuments.find((item) => item.id === documentId);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document?.name || 'download';
      link.style.display = 'none';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      onAttachmentError?.(preview ? 'Không thể xem trước file.' : 'Không thể tải file đính kèm.');
    }
  };

  const handleSubmit = async () => {
    const hadPendingFiles = selectedFiles.length > 0;
    const saved = await onSave(selectedFiles);
    if (saved !== false) {
      setSelectedFiles([]);
      onPendingFilesChange?.(false);

      const savedReceiptId = typeof saved === 'string' ? saved : receiptId;

      // The save handler returns the newly-created receipt id. Use it directly
      // because the receiptId prop is updated on the next React render only.
      if (savedReceiptId && hadPendingFiles) {
        setLoadingDocuments(true);
        try {
          const contentTypeId = await getContentTypeId('receipt');
          const documents = await getDocumentsByObject(contentTypeId, savedReceiptId);
          setUploadedDocuments(documents);
        } catch {
          onAttachmentError?.('Không thể tải lại danh sách file đính kèm.');
        } finally {
          setLoadingDocuments(false);
        }
      }
    }
  };
  React.useEffect(() => {
    let mounted = true;
    getItemUomBackendOptions()
      .then((options) => {
        if (!mounted) return;
        setUomOptions(options.map((option) => ({ id: option.id, name: option.name, code: option.name })));
      })
      .catch(() => {
        if (!mounted) return;
        setUomOptions([]);
      });
    return () => { mounted = false; };
  }, []);

  const handleDeleteLines = (lineIds: string[]) => {
    if (isReadOnly || !lineIds.length) return;
    const remainingLines = (formData.lines || []).filter((line) => !lineIds.includes(line.id));
    onChangeForm({ lines: remainingLines });
  };

  const handleAddLine = () => {
    if (isReadOnly) return;
    const currentLines = formData.lines || [];
    const nextSortOrder = currentLines.length > 0
      ? Math.max(...currentLines.map((line) => Number(line.sort_order) || 0)) + 1
      : 1;

    const newLine: ReceiptLine = {
      id: `manual-${Date.now()}`,
      sort_order: nextSortOrder,
      item_id: '',
      item_code: '',
      item_name: '',
      uom_id: '',
      uom_name: '',
      received_qty: '0',
      accepted_qty: '0',
      rejected_qty: '0',
      unit_cost: '0',
      line_amount: '0',
      purchase_line_id: null,
      lot_no: '',
      serial_numbers: '',
      note: '',
    };
    onChangeForm({ lines: [...currentLines, newLine] });
  };

  const handleReceiptTypeChange = (nextType: string) => {
    if (isReadOnly) return;
    onChangeForm({
      receipt_type: nextType,
      lines: [],
      purchase_id: null,
      purchase_no: null,
      return_source_issue_id: null,
      return_source_issue_no: null,
      supplier_id: '',
      supplier_name: '',
      status: defaultStatusId || '',
    });
  };

  return (
    <>
    <AppForm
      onSubmit={() => { void handleSubmit(); }}
      onCancel={() => {}}
      submitting={saving}
      submitDisabled={isReadOnly || saving || (!isDirty && selectedFiles.length === 0)}
      submitLabel='Lưu phiếu nhập'
      submittingLabel='Đang lưu...'
      containerSx={{
        m: 0,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflowX: 'hidden',
        ...(embedded ? {
          '& .MuiGrid-container': {
            width: '100%',
            maxWidth: '100%',
            marginLeft: 0,
            marginRight: 0,
          },
          '& .MuiGrid-root': {
            minWidth: 0,
            boxSizing: 'border-box',
          },
        } : {}),
      }}
      actionBarSx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        px: embedded ? 2 : 0,
        boxSizing: 'border-box',
        overflowX: 'hidden',
        flexWrap: 'wrap',
      }}
    >
      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Thông tin phiếu nhập</Typography>

        <Grid container rowSpacing={1} columnSpacing={5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Mã phiếu nhập' labelWidth={130}>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', fontFamily: '"Roboto Mono", monospace' }}>
                {formData.receipt_no || 'RC####-##'}
              </Typography>
            </LabeledField>
            <LabeledField label='Loại nhập' labelWidth={130}>
              <TextField
                select
                size='small'
                value={formData.receipt_type}
                onChange={(event) => handleReceiptTypeChange(event.target.value)}
                disabled={isReadOnly}
                fullWidth
              >
                <MenuItem value='from_purchase'>Nhập từ phiếu mua</MenuItem>
                <MenuItem value='direct'>Nhập trực tiếp</MenuItem>
                <MenuItem value='return'>Nhập trả về</MenuItem>
              </TextField>
            </LabeledField>
            {isFromPurchase && (
              <LabeledField label='PO nguồn' labelWidth={130}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%', minWidth: 0 }}>
                  <AddButtonAutocomplete
                    options={purchaseOptions}
                    value={selectedPurchase}
                    onChange={(_event, value) => {
                      const selected = value as { id: string; name: string } | null;
                      onChangeForm({ purchase_id: selected?.id || null, purchase_no: selected?.name || null });
                    }}
                    onInputChange={(_event, value, reason) => {
                      if (reason === 'input' || reason === 'clear') {
                        onPurchaseSearch(value);
                      }
                    }}
                    placeholder='Chọn phiếu mua hàng để tự sinh dòng nhập'
                    loading={purchaseLoading}
                    noOptionsText='Không tìm thấy phiếu mua hàng'
                    disabled={isReadOnly}
                    fullWidth
                    sx={{ width: '100%', minWidth: 0 }}
                  />
                </Box>
              </LabeledField>
            )}
            {isReturn && (
              <LabeledField label='Phiếu xuất nguồn' labelWidth={130}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%', minWidth: 0 }}>
                  <AddButtonAutocomplete
                    options={returnSourceOptions}
                    value={selectedReturnSource}
                    onChange={(_event, value) => {
                      const selected = value as { id: string; name: string } | null;
                      onChangeForm({
                        return_source_issue_id: selected?.id || null,
                        return_source_issue_no: selected?.name || null,
                      });
                    }}
                    onInputChange={(_event, value, reason) => {
                      if (reason === 'input' || reason === 'clear') {
                        onReturnSourceSearch(value);
                      }
                    }}
                    placeholder='Chọn phiếu xuất nguyên vật liệu để lấy dòng trả về'
                    loading={returnSourceLoading}
                    noOptionsText='Không tìm thấy phiếu xuất'
                    disabled={isReadOnly}
                    fullWidth
                    sx={{ width: '100%', minWidth: 0 }}
                  />
                </Box>
              </LabeledField>
            )}
            <LabeledField label='Nhà cung cấp' labelWidth={130}>
              <AddButtonAutocomplete
                options={effectiveSupplierOptions}
                value={selectedSupplier || (formData.supplier_id ? { id: formData.supplier_id, name: formData.supplier_name || '' } : null)}
                onChange={(_event, value) => {
                  const selected = value as { id: string; name: string } | null;
                  onChangeForm({
                    supplier_id: selected?.id || '',
                    supplier_name: selected?.name || '',
                  });
                }}
                CustomDialog={SupplierAddModal}
                canAddNew={!isReadOnly && !isFromPurchase}
                onAddNewCustom={async (data) => {
                  if (isReadOnly || isFromPurchase) {
                    return selectedSupplier || { id: '', name: '', code: '' };
                  }
                  const created = await createItemSupplierOption(data.name, formData.company_id, {
                    email: data.email,
                    phone: data.phone,
                    address: data.address,
                  });
                  const createdOption = { id: created.id, name: created.name, code: '' };
                  setManualSupplierOptions((current) => [...current, createdOption]);
                  onChangeForm({ supplier_id: created.id, supplier_name: created.name });
                  return createdOption;
                }}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    onSupplierSearch(value);
                  }
                }}
                placeholder={isFromPurchase ? 'Nhà cung cấp lấy theo PO' : 'Chọn nhà cung cấp'}
                loading={supplierLoading}
                noOptionsText='Không tìm thấy nhà cung cấp'
                disabled={isFromPurchase || isReadOnly}
                filterOptions={(options) => options}
                fullWidth
              />
            </LabeledField>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Kho nhập' labelWidth={130}>
              <WarehouseAutocomplete
                companyId={formData.company_id}
                value={formData.warehouse_id || ''}
                warehouseName={formData.warehouse_name || ''}
                disabled={isReadOnly}
                onInitialize={(warehouseId, warehouseName) => onInitializeForm?.({
                  warehouse_id: warehouseId,
                  warehouse_name: warehouseName,
                })}
                onChange={(warehouseId, warehouseName) => onChangeForm({
                  warehouse_id: warehouseId || '',
                  warehouse_name: warehouseName || '',
                })}
              />
            </LabeledField>
            <LabeledField label='Nhân viên' labelWidth={130}>
              <Autocomplete
                sx={sharedFormStyles.inputArrowAlign}
                options={employeeOptions}
                loading={employeeLoading}
                value={selectedEmployee}
                onChange={(_event, newValue) => {
                  const employee = newValue as RequestMaterialEmployeeOption | null;
                  onChangeForm({
                    employee_id: employee?.id || '',
                    employee_name: employee?.name || '',
                  });
                }}
                getOptionLabel={(option) => option.nickname || option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                disabled={isReadOnly}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder='Chọn nhân viên'
                    size='small'
                    variant='standard'
                  />
                )}
                renderOption={(props, option) => (
                  <Box component='li' {...props} key={option.id}>
                    <UserOptionDisplay
                      user={{
                        id: option.id,
                        name: option.name,
                        nickname: option.nickname,
                        avatar: option.avatar,
                        image1: option.image1,
                      }}
                    />
                  </Box>
                )}
                noOptionsText='Không tìm thấy nhân viên'
                fullWidth
              />
            </LabeledField>
            <LabeledField label='Trạng thái' labelWidth={130}>
              <Autocomplete
                options={effectiveStatusOptions}
                value={currentStatus}
                onChange={(_event, newValue) => {
                  const selectedStatus = newValue as { id?: string; name: string; code?: string; color?: string } | null;
                  onChangeForm({
                    status: selectedStatus?.id || '',
                    status_code: selectedStatus?.code || '',
                    status_display: selectedStatus?.name || '',
                    status_color: selectedStatus?.color || '',
                  });
                }}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                disabled={isReadOnly}
                renderOption={(props, option) => (
                  <li {...props} key={option.id || option.name} style={{ padding: 0, margin: 0 }}>
                    <Typography sx={{ color: option.color || 'inherit', paddingLeft: '24px', py: 0.5 }}>
                      {option.name}
                    </Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder='Chọn trạng thái'
                    size='small'
                    variant='standard'
                    InputProps={{ ...params.InputProps }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: currentStatus?.color || formData.status_color || 'inherit',
                        fontWeight: currentStatus?.color || formData.status_color ? 500 : 'inherit',
                      },
                    }}
                  />
                )}
                noOptionsText='Không tìm thấy trạng thái'
                fullWidth
              />
            </LabeledField>
            <LabeledField label='Thời điểm nhận' labelWidth={130}>
              <DatePicker
                value={receivedDate && !isNaN(receivedDate.getTime()) ? receivedDate : null}
                onChange={(value) => onChangeForm({ received_at: value ? value.toISOString() : '' })}
                disabled={isReadOnly}
                format='dd/MM/yyyy'
                slotProps={{
                  textField: { size: 'small', fullWidth: true },
                }}
              />
            </LabeledField>
          </Grid>
        </Grid>
      </Paper>

      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          px: embedded ? 2 : 0,
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}
      >
      <Box sx={{ width: '100%' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Danh sách vật tư</Typography>
        <ReceiptLinesTable
          lines={formData.lines || []}
          onChangeLine={onChangeLine}
          onDeleteLines={handleDeleteLines}
          itemOptions={itemOptions}
          uomOptions={uomOptions}
          itemLoading={itemLoading}
          allowAddItem={!isReadOnly && formData.receipt_type !== 'from_purchase'}
          onAddItem={handleAddLine}
          disabled={isReadOnly}
          companyId={formData.company_id}
          onCreateItem={addItemOption}
          canCreateItem={!isReadOnly}
          searchItems={searchItemOptions}
          searchLoading={itemSearchLoading}
        />
      </Box>

      <Box sx={{ mt: 2, width: '100%' }}>
        <LabeledField label='Ghi chú' alignItems='start' labelWidth={80}>
          <TextareaAutosize
            minRows={2}
            value={formData.note || ''}
            onChange={(event) => onChangeForm({ note: event.target.value })}
            disabled={isReadOnly}
            spellCheck={false}
            autoComplete='off'
            readOnly={isReadOnly}
            style={sharedFormStyles.additionalTextarea(!isReadOnly)}
          />
        </LabeledField>
      </Box>
      <Grid container rowSpacing={0} columnSpacing={4}>
        <Grid size={{ xs: 12 }}>
          <LabeledField label='File đính kèm'>
            <FileAttachmentField
              documents={uploadedDocuments}
              selectedFiles={selectedFiles}
              disabled={isReadOnly || saving}
              loading={loadingDocuments}
              fileInputKey={`receipt-file-input-${receiptId || 'new'}`}
              onFileSelect={handleFileSelect}
              onRemoveFile={handleRemoveFile}
              onRemoveDocument={handleRemoveUploadedDocument}
              onPreview={(documentId) => { void handleDocumentDownload(documentId, true); }}
              onDownload={(documentId) => { void handleDocumentDownload(documentId); }}
            />
          </LabeledField>
        </Grid>
      </Grid>
      </Box>
    </AppForm>
    <DeleteConfirmationModal
      open={pendingDeleteType !== null}
      onClose={cancelDeleteAttachment}
      onConfirm={confirmDeleteAttachment}
      title='Xóa file đính kèm'
      message='Bạn có chắc chắn muốn xóa file này?'
      confirmText='Xóa'
      cancelText='Hủy'
    />
    </>
  );
}
